// Email Service for Cospin Website
// Sends emails via backend server using Brevo API

class EmailService {
  constructor() {
    // API key is handled securely on the backend (api/contact.js)
    this.fromEmail = 'admin@cospin.in'; // Verified sender email in Brevo
    this.toEmail = 'admin@cospin.in';
    // For Vercel deployment, use relative paths to API routes
    this.backendUrl = '';
  }

  // Send contact form email
  async sendContactFormEmail(formData) {
    try {
      const response = await fetch(`/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          company: formData.company || '',
          service: formData.service || '',
          message: formData.message
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Contact form submission error:', error);
      throw new Error(`Failed to submit contact form: ${error.message}`);
    }
  }

  // Send career form email with resume
  async sendCareerFormEmail(formData, resumeFile) {
    try {
      console.log('EmailService: Preparing career form data');
      console.log('Form data:', formData);
      console.log('Resume file:', resumeFile);

      const formDataToSend = new FormData();
      formDataToSend.append('firstName', formData.name.split(' ')[0] || formData.name);
      formDataToSend.append('lastName', formData.name.split(' ').slice(1).join(' ') || '');
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone || '');
      formDataToSend.append('position', formData.position || '');
      formDataToSend.append('notice_period', formData.notice_period || '');
      formDataToSend.append('current_ctc', formData.current_ctc || '');
      formDataToSend.append('expected_ctc', formData.expected_ctc || '');
      formDataToSend.append('message', formData.message || 'Career opportunity inquiry');
      formDataToSend.append('resume', resumeFile);

      console.log('FormData contents:');
      for (let [key, value] of formDataToSend.entries()) {
        console.log(`${key}:`, value);
      }

      const response = await fetch(`/api/career`, {
        method: 'POST',
        body: formDataToSend
      });

      console.log('Response status:', response.status);
      console.log('Response ok:', response.ok);

      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch (parseError) {
          console.error('Failed to parse error response:', parseError);
          errorData = { message: `HTTP error! status: ${response.status}` };
        }
        console.error('Error response:', errorData);
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      let result;
      try {
        result = await response.json();
      } catch (parseError) {
        console.error('Failed to parse success response:', parseError);
        throw new Error('Invalid response format from server');
      }

      return result;
    } catch (error) {
      console.error('Career form submission error:', error);
      throw new Error(`Failed to submit career form: ${error.message}`);
    }
  }

  // Send lead form email
  async sendLeadFormEmail(formData, caseStudyName) {
    try {
      const response = await fetch(`/api/lead`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone || '',
          company: formData.company || '',
          caseStudyName: caseStudyName || 'Case Study'
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Lead form submission error:', error);
      throw new Error(`Failed to submit lead form: ${error.message}`);
    }
  }

  // Send case study email
  async sendCaseStudyEmail(formData, caseStudyData) {
    try {
      console.log('EmailService: Preparing case study form data');
      console.log('Form data:', formData);
      console.log('Case study data:', caseStudyData);

      const response = await fetch(`/api/case-study`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone || '',
          company: formData.company || '',
          caseStudyName: caseStudyData.title || 'Case Study',
          caseStudyId: caseStudyData.id || '',
          filePath: caseStudyData.filePath || ''
        })
      });

      console.log('Case study response status:', response.status);
      console.log('Case study response ok:', response.ok);

      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch (parseError) {
          console.error('Failed to parse case study error response:', parseError);
          errorData = { message: `HTTP error! status: ${response.status}` };
        }
        console.error('Case study error response:', errorData);
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      let result;
      try {
        result = await response.json();
      } catch (parseError) {
        console.error('Failed to parse case study success response:', parseError);
        throw new Error('Invalid response format from server');
      }

      return result;
    } catch (error) {
      console.error('Case study email submission error:', error);
      throw new Error(`Failed to submit case study form: ${error.message}`);
    }
  }

  // Send annual return request email
  async sendAnnualReturnEmail(formData) {
    try {
      const response = await fetch(`/api/annual-return`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          financialYear: formData.financialYear,
          relationship: formData.relationship,
          purpose: formData.purpose,
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Annual return form submission error:', error);
      throw new Error(`Failed to submit annual return request: ${error.message}`);
    }
  }

  // Core email sending function (now calls backend)
  async sendEmail(subject, htmlContent, textContent) {
    try {
      // For direct email sending, we'll use the contact endpoint
      const response = await fetch(`${this.backendUrl}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          firstName: 'System',
          lastName: 'User',
          email: 'admin@cospin.in',
          company: 'Cospin',
          service: 'General',
          message: `Subject: ${subject}\n\nContent:\n${textContent}`
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Email sending error:', error);
      throw new Error(`Failed to send email: ${error.message}`);
    }
  }

  // Send email with attachment
  async sendEmailWithAttachment(formData, attachmentFile) {
    try {
      const formDataToSend = new FormData();

      // Add form fields
      Object.keys(formData).forEach(key => {
        formDataToSend.append(key, formData[key]);
      });

      // Add attachment if present
      if (attachmentFile) {
        formDataToSend.append('attachment', attachmentFile);
      }

      const response = await fetch(`/api/send-email-with-attachment`, {
        method: 'POST',
        body: formDataToSend
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Email with attachment error:', error);
      throw new Error(`Failed to send email with attachment: ${error.message}`);
    }
  }

  // Test email configuration
  async testConnection() {
    try {
      const response = await fetch(`${this.backendUrl}/api/email/test`);

      if (!response.ok) {
        const errorData = await response.json();
        return {
          success: false,
          message: errorData.message || `HTTP error! status: ${response.status}`
        };
      }

      const result = await response.json();
      return result;
    } catch (error) {
      return {
        success: false,
        message: `Connection error: ${error.message}`
      };
    }
  }

  // Test backend health
  async testBackendHealth() {
    try {
      const response = await fetch(`${this.backendUrl}/api/health`);

      if (!response.ok) {
        return {
          success: false,
          message: `Backend unhealthy: ${response.status}`
        };
      }

      const result = await response.json();
      return result;
    } catch (error) {
      return {
        success: false,
        message: `Backend connection failed: ${error.message}`
      };
    }
  }
}

// Create singleton instance
const emailService = new EmailService();

export default emailService;
